import React, { useState, useEffect, useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { FaTrash, FaPlus, FaSun, FaCloudSun, FaCloudRain, FaCloudShowersHeavy, FaQuestionCircle } from 'react-icons/fa';
import { preventInvalidKeys, blurOnWheel, preventInvalidPaste } from 'src/reusable/inputSettings';
import { getReport, CreateDailyReport, UpdateDailyReport, getDetails, ArchiveDailyReport } from 'src/request/Reports';
import ReportLayout from './components/ReportLayout';
import { getSowList } from 'src/request/Projects';

const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const DailyReportV2 = (props) => {
  const [dailyReports, setDailyReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(0);
  const [modalMode, setModalMode] = useState('Add');
  const [reportLogId, setReportLogId] = useState(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentList, setAttachmentList] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [removedReportIds, setRemovedReportIds] = useState([]);
  const [weatherData, setWeatherData] = useState([]);
  const [date, setDate] = useState(getTodayDateString());
  const company_id = parseInt(localStorage.getItem('company_id'));
  const [sowList, setSowList] = useState([]);
  const [isDescriptionValid, setIsDescriptionValid] = useState(false);
  const [isHoursValid, setIsHoursValid] = useState(false);
  const [isActivityValid, setIsActivityValid] = useState(false);

  const [reportItems, setReportItems] = useState([
    {
      project_id: '',
      activity: '',
      hours: '',
      work: '',
    },
  ]);

  const selectCustomStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '34px',
      fontSize: '12px',
      borderRadius: '0.375rem',
      borderColor: state.isFocused ? '#23303D' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(35, 48, 61, 0.2)' : 'none',
      backgroundColor: state.isDisabled ? '#F3F4F6' : '#FFFFFF',
      '&:hover': {
        borderColor: state.isFocused ? '#23303D' : '#9CA3AF',
      },
    }),

    placeholder: (base) => ({
      ...base,
      color: '#9CA3AF',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),

    menu: (base) => ({
      ...base,
      fontSize: '12px',
      zIndex: 50,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#23303D'
        : state.isFocused
          ? '#F3F4F6'
          : '#FFFFFF',
      color: state.isSelected ? '#FFFFFF' : '#23303D',
      cursor: 'pointer',
    }),
  };


  useEffect(() => {
    fetchDailyReports(0);
    fetchSowList()
  }, [props.id]);

  const fetchSowList = async () => {
    try {
      const res = await getSowList({
        project_id: props.id,
        company_id,
      });

      if (res.data.status === 1) {
        const result = res.data.response.flatMap(item =>
          item.Task.map(i => i.SOW)
        );
        setSowList(result);
      }

    } catch (error) {
      console.error('Error fetching SOW list:', error);
    }
  };

  const fetchDailyReports = (selectedStatus) => {
    setIsLoading(true);
    getReport(props.id, 'daily', selectedStatus)
      .then((res) => {
        if (res.data?.status == 1) {
          setDailyReports(res.data.response || []);
        } else {
          Swal.fire({
            icon: 'error',
            title: res.data?.response || 'Error',
            text: res.data?.description || 'Failed to fetch daily reports',
            confirmButtonColor: '#23303D',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append('project_id', props.id);

    if (date) {
      formData.append('date', date);
    }

    reportItems.forEach((item) => {
      formData.append('activity', item?.activity || '');
      formData.append('hours', item?.hours !== '' && item?.hours !== null ? item.hours : '');
      formData.append('work', item?.work || '');
    });

    for (let i = 0; i < attachmentFiles.length; i++) {
      formData.append('attachment_files', attachmentFiles[i]);
    }

    CreateDailyReport(formData).then((res) => {
      if (res.status === 200 || res.data?.status == 1) {
        Swal.fire({
          icon: 'success',
          title: 'Your work has been saved',
          showConfirmButton: false,
          timer: 1500,
        });
        setIsModalOpen(false);
        fetchDailyReports(statusFilter);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res.data?.description || res.data?.error || 'Failed to save daily report',
          confirmButtonColor: '#23303D',
        });
      }
    });
  };

  const handleOpenReportDetails = (selectedReportLogId, mode = 'View') => {
    setReportLogId(selectedReportLogId);
    setModalMode(mode);
    setAttachmentFiles([]);
    setRemovedAttachmentIds([]);
    setRemovedReportIds([]);
    getDetails('daily', selectedReportLogId).then((res) => {
      if (res.data?.status == 1) {
        const responseData = res.data?.response;
        const entries = Array.isArray(responseData)
          ? responseData
          : responseData?.entries || [];

        const mappedItems = entries.length > 0
          ? entries.map((entry) => ({
            report_id: entry.report_id || '',
            activity: entry.activity || '',
            hours: entry.hours !== undefined && entry.hours !== null ? entry.hours : '',
            work: entry.work || '',
            report_log_id: entry.report_log_id || selectedReportLogId,
          }))
          : [
            {
              report_id: '',
              activity: '',
              hours: '',
              work: '',
              report_log_id: selectedReportLogId,
            },
          ];

        setReportItems(mappedItems);

        const rawAttachments = Array.isArray(responseData?.attachments)
          ? responseData.attachments
          : responseData?.[0]?.attachments || [];

        const existingAttachments = rawAttachments.map((att) => ({
          id: att.id,
          name: att.filename || att.name,
          link: att.url || att.link,
          isExisting: true,
        }));
        setAttachmentList(existingAttachments);

        setWeatherData(responseData?.weather_data || []);
        const rawDate = responseData?.date || responseData?.date_recorded;
        const formattedDate = rawDate
          ? (rawDate.includes('T') ? rawDate.split('T')[0] : rawDate.split(' ')[0])
          : '';
        setDate(formattedDate);

        setIsActivityValid(true);
        setIsHoursValid(true);
        setIsDescriptionValid(true);
        setIsModalOpen(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: res.data?.response || 'Error',
          text: res.data?.description || 'Failed to fetch report details',
          confirmButtonColor: '#23303D',
        });
      }
    });
  };

  const handleUpdateReport = () => {
    const formData = new FormData();
    formData.append('report_log_id', reportLogId);

    if (date) {
      formData.append('date', date);
    }

    reportItems.forEach((item) => {
      const isExistingRecord = item.report_id && Number(item.report_id) > 0;
      const reportId = isExistingRecord ? item.report_id : -1;
      formData.append('report_id', reportId);
      formData.append('activity', item?.activity || '');
      formData.append('hours', item?.hours !== '' && item?.hours !== null ? item.hours : '');
      formData.append('work', item?.work || '');
    });

    if (removedAttachmentIds.length > 0) {
      formData.append('attachments_to_remove', JSON.stringify(removedAttachmentIds));
    }

    if (removedReportIds.length > 0) {
      formData.append('reports_to_remove', JSON.stringify(removedReportIds));
    }

    for (let i = 0; i < attachmentFiles.length; i++) {
      formData.append('attachment_files', attachmentFiles[i]);
    }

    UpdateDailyReport(formData).then((res) => {
      if (res.status === 200 || res.data?.status == 200 || res.data?.status == 1) {
        Swal.fire({
          icon: 'success',
          title: 'Your work has been updated',
          showConfirmButton: false,
          timer: 1500,
        });
        fetchDailyReports(statusFilter);
        setIsModalOpen(false);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res.data?.description || res.data?.error || res.data?.message || 'Failed to update daily report',
          confirmButtonColor: '#23303D',
        });
      }
    });
  };

  const handleChangeReportStatus = (selectedReportLogId, method) => {
    const isArchiving = method === 'archive';
    Swal.fire({
      icon: 'question',
      title: `<span style='color:#B42A19;'>${isArchiving ? 'Archive Report?' : 'Restore Report?'}</span>`,
      html: `<p style='font-weight:600;'>Are you sure you want to ${isArchiving ? 'archive' : 'restore'} this report?</p>`,
      showCancelButton: true,
      confirmButtonColor: '#23303D',
      cancelButtonColor: '#B42A19',
      confirmButtonText: isArchiving ? 'Yes, archive' : 'Yes, restore',
    }).then(async (result) => {
      const payload = {
        project_id: Number(props.id),
        report_log_id: selectedReportLogId,
        is_status: isArchiving ? 1 : 0,
      };

      if (result.isConfirmed) {
        try {
          const res = await ArchiveDailyReport(payload);
          if (res.data?.status === 200) {
            Swal.fire({
              icon: 'success',
              title: isArchiving ? 'Report has been archived' : 'Report has been restored',
              showConfirmButton: false,
              timer: 1500,
            });
            fetchDailyReports(statusFilter);
          } else {
            Swal.fire({
              icon: 'error',
              title: res.data?.response || 'Error',
              text: res.data?.description || 'Failed to update report status',
              confirmButtonColor: '#23303D',
            });
          }
        } catch (error) {
          console.error('Something went wrong. Please try again', error);
        }
      }
    });
  };

  const handleUploadFiles = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = [...attachmentFiles, ...selectedFiles];
    setAttachmentFiles(newFiles);

    const newDisplayList = selectedFiles.map((file) => ({
      name: file.name,
      link: '',
      isExisting: false,
      fileRef: file,
    }));
    setAttachmentList([...attachmentList, ...newDisplayList]);
    e.target.value = '';
  };

  const handleRemoveAttachment = (item, index) => {
    const updatedFileList = [...attachmentList];
    updatedFileList.splice(index, 1);
    setAttachmentList(updatedFileList);

    if (item.isExisting && item.id) {
      setRemovedAttachmentIds([...removedAttachmentIds, item.id]);
    } else {
      const fileIndex = attachmentFiles.findIndex((f) => f === item.fileRef || f.name === item.name);
      if (fileIndex !== -1) {
        const updatedFiles = [...attachmentFiles];
        updatedFiles.splice(fileIndex, 1);
        setAttachmentFiles(updatedFiles);
      }
    }
  };

  const handleOpenAddModal = () => {
    setModalMode('Add');
    setAttachmentFiles([]);
    setAttachmentList([]);
    setRemovedAttachmentIds([]);
    setRemovedReportIds([]);
    setWeatherData([]);
    setDate(getTodayDateString());
    setReportItems([
      {
        report_id: '',
        activity: '',
        hours: '',
        work: '',
        report_log_id: reportLogId,
      },
    ]);
    setIsActivityValid(false);
    setIsHoursValid(false);
    setIsDescriptionValid(false);
    setIsModalOpen(true);
  };

  const handleAddReportRow = () => {
    const updatedItems = [...reportItems];
    updatedItems.push({
      report_id: -1,
      activity: '',
      hours: '',
      work: '',
      report_log_id: reportLogId,
    });
    setIsActivityValid(false);
    setIsHoursValid(false);
    setIsDescriptionValid(false);
    setReportItems(updatedItems);
  };

  const handleActivityChange = (newValue, index) => {
    const value = typeof newValue === 'object' && newValue !== null ? newValue.value : (newValue || '');
    const updatedItems = [...reportItems];
    updatedItems[index].activity = value;
    setIsActivityValid(updatedItems.every((item) => !!item.activity?.trim()));
    setReportItems(updatedItems);
  };

  const handleHoursChange = (e, index) => {
    const updatedItems = [...reportItems];
    updatedItems[index].hours = e.target.value;
    setIsHoursValid(updatedItems.every((item) => item.hours !== '' && item.hours !== null && Number(item.hours) >= 0));
    setReportItems(updatedItems);
  };

  const handleDescriptionChange = (e, index) => {
    const updatedItems = [...reportItems];
    updatedItems[index].work = e.target.value;
    setIsDescriptionValid(updatedItems.every((item) => !!item.work?.trim()));
    setReportItems(updatedItems);
  };

  const handleRemoveReportRow = (index) => {
    const itemToRemove = reportItems[index];
    if (itemToRemove?.report_id && itemToRemove.report_id !== -1) {
      setRemovedReportIds((prev) => [...prev, itemToRemove.report_id]);
    }
    const updatedItems = [...reportItems];
    updatedItems.splice(index, 1);
    setIsActivityValid(updatedItems.length > 0 && updatedItems.every((item) => !!item.activity?.trim()));
    setIsHoursValid(updatedItems.length > 0 && updatedItems.every((item) => item.hours !== '' && item.hours !== null));
    setIsDescriptionValid(updatedItems.length > 0 && updatedItems.every((item) => !!item.work?.trim()));
    setReportItems(updatedItems);
  };

  const sowOptions = useMemo(() => {
    return (sowList || []).map((sow) => ({
      label: sow,
      value: sow,
    }));
  }, [sowList]);

  const isDateValid = modalMode === 'View' || !!date?.trim?.();
  const isFormValid = isDescriptionValid && isHoursValid && isActivityValid && isDateValid;

  return (
    <ReportLayout
      title="Daily Progress Report"
      addButtonLabel="Add New Report"
      onAddNew={handleOpenAddModal}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search reports by date or author..."
      statusFilter={statusFilter}
      onStatusFilterChange={(val) => {
        setStatusFilter(val);
        fetchDailyReports(val);
      }}
      columns={[
        { key: 'date', label: 'Date' },
        { key: 'submitted', label: 'Submitted By' },
        { key: 'last_modified', label: 'Last Modified', className: 'text-aimhi-textlight-muted' },
      ]}
      data={dailyReports}
      isLoading={isLoading}
      emptyMessage="No daily reports found"
      emptySubMessage={
        searchTerm
          ? 'Try clearing your search query'
          : statusFilter === 0
            ? 'Create your first daily progress report to get started'
            : 'No archived reports available'
      }
      onView={(report) => handleOpenReportDetails(report.report_log_id, 'View')}
      onEdit={(report) => handleOpenReportDetails(report.report_log_id, 'Edit')}
      onArchive={(report) => handleChangeReportStatus(report.report_log_id, 'archive')}
      onRestore={(report) => handleChangeReportStatus(report.report_log_id, 'restore')}
      isModalOpen={isModalOpen}
      onCloseModal={() => setIsModalOpen(false)}
      modalMode={modalMode}
      modalTitle={modalMode === 'Add' ? 'New Daily Progress Report' : modalMode === 'Edit' ? 'Edit Daily Progress Report' : 'Daily Progress Report Details'}
      modalSubtitle={modalMode === 'View' ? 'Review recorded work items, hours, and attachments.' : 'Fill in the activity details and optional attachments.'}
      renderModalContent={() => (
        <>
          <div className="mb-4">
            <label className="block text-xs font-semibold text-aimhi-textlight-neutral mb-1.5">
              Date {modalMode !== 'View' && <span className="text-aimhi-danger">*</span>}
            </label>
            <input
              type="date"
              value={date || ''}
              disabled={modalMode === 'View'}
              onChange={(e) => setDate(e.target.value)}
              className="w-full sm:w-64 px-2.5 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-aimhi-textlight-neutral focus:outline-none focus:ring-2 focus:ring-aimhi-info/20 focus:border-aimhi-info disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>

          {weatherData && weatherData.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs font-semibold text-aimhi-textlight-neutral mb-2">
                Weather Conditions
              </div>
              <div className="flex flex-wrap gap-2">
                {weatherData.map((weather, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-2 px-2.5 py-1 text-xs rounded-md bg-white border border-gray-200 text-aimhi-textlight-neutral shadow-xs"
                  >
                    <span className="text-aimhi-textlight-muted">{weather.message || weather.time}</span>
                    {(() => {
                      const status = weather.weather_status;
                      let icon = <FaQuestionCircle className="text-gray-400 text-sm" />;
                      let label = 'No Forecast';

                      if (status === 'NoRain') {
                        icon = <FaSun className="text-amber-500 text-sm" />;
                        label = 'No Rain';
                      } else if (status === 'ModerateRain') {
                        icon = <FaCloudRain className="text-blue-500 text-sm" />;
                        label = 'Moderate Rain';
                      } else if (status === 'HeavyRain') {
                        icon = <FaCloudShowersHeavy className="text-blue-700 text-sm" />;
                        label = 'Heavy Rain';
                      } else if (status) {
                        icon = <FaCloudSun className="text-blue-400 text-sm" />;
                        label = status;
                      }

                      return (
                        <span className="inline-flex items-center gap-1.5" title={label}>
                          {icon}
                          <span className="text-[11px] font-medium text-aimhi-textlight-neutral">{label}</span>
                        </span>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg mb-4">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-aimhi-light border-b border-gray-200 text-[11px] font-semibold text-aimhi-textlight-neutral uppercase">
                  <th className="py-2.5 px-3 min-w-[180px]">
                    Scope of Work / Activity <span className="text-aimhi-danger">*</span>
                  </th>
                  <th className="py-2.5 px-3 w-28">
                    Hours <span className="text-aimhi-danger">*</span>
                  </th>
                  <th className="py-2.5 px-3 min-w-[220px]">
                    Description of Work <span className="text-aimhi-danger">*</span>
                  </th>
                  {modalMode !== 'View' && <th className="py-2.5 px-3 w-12 text-center">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {reportItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/50">
                    <td className="py-2 px-3 align-top">
                      <CreatableSelect
                        isClearable
                        isDisabled={modalMode === 'View'}
                        placeholder="Select or enter activity / SOW..."
                        value={item.activity ? { label: item.activity, value: item.activity } : null}
                        options={sowOptions}
                        onChange={(option) => handleActivityChange(option, index)}
                        onCreateOption={(inputValue) => handleActivityChange({ label: inputValue, value: inputValue }, index)}
                        styles={selectCustomStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    </td>
                    <td className="py-2 px-3 align-top">
                      <input
                        type="number"
                        placeholder="0"
                        min="0"
                        step="0.5"
                        value={item.hours ?? ''}
                        disabled={modalMode === 'View'}
                        onKeyDown={preventInvalidKeys}
                        onWheel={blurOnWheel}
                        onPaste={preventInvalidPaste}
                        onChange={(e) => handleHoursChange(e, index)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-aimhi-textlight-neutral placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-aimhi-info/20 focus:border-aimhi-info disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </td>
                    <td className="py-2 px-3 align-top">
                      <textarea
                        rows={2}
                        placeholder="Describe progress and tasks completed..."
                        value={item.work || ''}
                        disabled={modalMode === 'View'}
                        onChange={(e) => handleDescriptionChange(e, index)}
                        className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-aimhi-textlight-neutral placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-aimhi-info/20 focus:border-aimhi-info disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </td>
                    {modalMode !== 'View' && (
                      <td className="py-2 px-3 align-middle text-center">
                        {reportItems.length > 1 && (
                          <button
                            type="button"
                            title="Remove row"
                            onClick={() => handleRemoveReportRow(index)}
                            className="p-1 rounded text-gray-400 hover:text-aimhi-danger hover:bg-aimhi-danger/10 transition-colors cursor-pointer"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {modalMode !== 'View' && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handleAddReportRow}
                disabled={!isFormValid}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-aimhi-textlight-neutral hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-0 p-0"
              >
                <FaPlus className="text-[10px]" />
                <span>Add More Activity</span>
              </button>
            </div>
          )}
        </>
      )}
      enableAttachments
      attachments={attachmentList}
      onUploadFiles={handleUploadFiles}
      onRemoveAttachment={handleRemoveAttachment}
      attachmentFileInputId="daily-file-input"
      isFormValid={isFormValid}
      onSubmit={handleSubmit}
      onUpdate={handleUpdateReport}
    />
  );
};


export default DailyReportV2;

